import { useState } from 'react'
import { X, Calendar, Check, AlertTriangle } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { usePOSStore } from '@/stores/posStore'

const MINIMUM_AGE = 19 // Ontario legal age

export function AgeVerifyModal() {
  const { closeModal, modalData, showToast } = useUIStore()
  const { selectedCustomer, setCustomer } = usePOSStore()
  const [birthdate, setBirthdate] = useState('')
  const [isVerified, setIsVerified] = useState<boolean | null>(null)

  const onVerified = modalData?.onVerified as (() => void) | undefined

  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    
    return age
  }

  const handleVerify = () => {
    if (!birthdate) {
      showToast({ type: 'error', title: 'Missing Date', message: 'Please enter a birthdate.' })
      return
    }

    const age = calculateAge(birthdate)
    
    if (age >= MINIMUM_AGE) {
      setIsVerified(true)
      
      // Update customer with verified status if we have one
      if (selectedCustomer) {
        setCustomer({
          ...selectedCustomer,
          is_verified: true,
          birthdate,
        })
      }
      
      // Callback to proceed
      setTimeout(() => {
        onVerified?.()
        closeModal()
      }, 1500)
    } else {
      setIsVerified(false)
      showToast({
        type: 'error',
        title: 'Age Verification Failed',
        message: `Customer must be ${MINIMUM_AGE} or older to purchase cannabis.`,
        duration: 5000,
      })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeModal}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Calendar size={20} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Age Verification</h2>
          </div>
          <button
            onClick={closeModal}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isVerified === null && (
            <>
              <p className="text-gray-600 mb-4">
                Please verify the customer's age before completing this cannabis purchase. 
                Customer must be <strong>{MINIMUM_AGE} years or older</strong>.
              </p>

              {selectedCustomer?.birthdate ? (
                <div className="p-4 bg-gray-50 rounded-xl mb-4">
                  <p className="text-sm text-gray-500 mb-1">Birthdate on file:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedCustomer.birthdate).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Age: {calculateAge(selectedCustomer.birthdate)} years old
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Customer's Birthdate
                  </label>
                  <input
                    type="date"
                    value={birthdate}
                    onChange={(e) => setBirthdate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                  />
                </div>
              )}

              <button
                onClick={() => {
                  if (selectedCustomer?.birthdate) {
                    setBirthdate(selectedCustomer.birthdate)
                  }
                  handleVerify()
                }}
                disabled={!birthdate && !selectedCustomer?.birthdate}
                className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Verify Age
              </button>
            </>
          )}

          {isVerified === true && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-600 mb-2">Age Verified</h3>
              <p className="text-gray-500">Customer is {calculateAge(birthdate)} years old.</p>
            </div>
          )}

          {isVerified === false && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-red-600 mb-2">Sale Restricted</h3>
              <p className="text-gray-500">
                Customer must be {MINIMUM_AGE} years or older to purchase cannabis.
              </p>
              <button
                onClick={closeModal}
                className="mt-4 px-6 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
